import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api/axios';

const FOOTER_FALLBACK = {
  quote:
    'St. Andrews World School is dedicated to shaping tomorrow\'s leaders by providing an education beyond academics. With seven campuses across India across India, our mission is to foster curiosity, ignite a passion for learning, and empower students to reach their full potential.',
  navigation: [
    { title: 'About Us', url: '/about-us' },
    { title: 'Curriculum', url: '/curriculum' },
    { title: 'Admission', url: '/admission' },
    { title: 'Gallery', url: '/gallery' },
    { title: 'Contact Us', url: '/contact-us' },
  ],
  social: [
    { title: 'Facebook', key: 'facebook', icon: 'fab fa-facebook-f' },
    { title: 'Twitter', key: 'twitter', icon: 'fab fa-x-twitter' },
    { title: 'Instagram', key: 'instagram', icon: 'fab fa-instagram' },
    { title: 'LinkedIn', key: 'linkedin', icon: 'fab fa-linkedin-in' },
  ],
  contact: {
    address: 'Block C, Ansal Sushant City, Sector 61, Kundli, Sonipat, Haryana 131028',
    phone: '9266333940, 9266333941',
    email: 'info@sawssonipat.com',
  },
  copyright: 'St. Andrews World School. All rights reserved.',
};

const Footer = () => {
  const [settings, setSettings] = useState({});
  const [contacts, setContacts] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingRes, contactRes] = await Promise.all([
          API.get('/settings/public'),
          API.get('/contacts/public')
        ]);
        setSettings(settingRes.data.data || {});
        setContacts(contactRes.data.data || {});
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  const effectiveNav = FOOTER_FALLBACK.navigation;

  const effectiveSocial = FOOTER_FALLBACK.social;

  const effectiveAddress = contacts.address || FOOTER_FALLBACK.contact.address;
  const effectivePhone = contacts.phone || FOOTER_FALLBACK.contact.phone;
  const effectiveEmail = contacts.email || FOOTER_FALLBACK.contact.email;

  const socialHref = (key) => {
    const urls = {
      facebook: 'https://www.facebook.com/share/1LWoXSJynp/?mibextid=wwXIfr',
      instagram: 'https://www.instagram.com/saws.sonipat?igsh=MXNzbGd4eWNpZXRmeA%3D%3D&utm_source=qr',
      youtube: 'https://youtube.com/@sawssonipat?si=Kn-dJOGMkaDYdsQX',
    };
    return urls[key] || contacts[key] || '#';
  };

  return (
    <footer className="bg-[#1b3160] text-white/80 pt-14 pb-0 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-secondary/5 blur-3xl"></div>
        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#1b3160_1px,transparent_1px)] [background-size:32px_32px]"></div>
      </div>
      <div className="container relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1: About School */}
          <div>
            <h3 className="text-white tracking-[0.14em] uppercase text-[15px] font-semibold mb-2">About School</h3>
            <div className="w-10 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mb-4"></div>
            <p className="text-white leading-relaxed text-sm">
              {settings.footer_text || FOOTER_FALLBACK.quote}
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 className="text-white tracking-[0.14em] uppercase text-[15px] font-semibold mb-2">Navigation</h3>
            <div className="w-10 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mb-4"></div>
            <ul className="space-y-2.5">
              {effectiveNav.map((m) => (
                <li key={m.url}>
                  <Link to={m.url} className="text-white/80 hover:text-white transition-colors text-sm">
                    {m.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Social */}
          <div>
            <h3 className="text-white tracking-[0.14em] uppercase text-[15px] font-semibold mb-2">Social Media</h3>
            <div className="w-10 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mb-4"></div>
            <ul className="space-y-2.5">
              {effectiveSocial.map((s) => (
                <li key={s.key}>
                  <a
                    href={socialHref(s.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-2 text-sm"
                    aria-label={s.title}
                  >
                    <i className={`${s.icon} text-[12px] w-5 text-center`} aria-hidden />
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h3 className="text-white tracking-[0.14em] uppercase text-[15px] font-semibold mb-2">Contact Us</h3>
            <div className="w-10 h-[3px] bg-gradient-to-r from-secondary to-secondary-light rounded-full mb-4"></div>
            <div className="space-y-3 text-white leading-relaxed text-sm">
              <p className="text-white">
                <span className="text-secondary-light text-xs font-semibold uppercase tracking-wider block mb-0.5">Address</span>
                <span className="text-white">{effectiveAddress}</span>
              </p>
              <p className="text-white">
                <span className="text-secondary-light text-xs font-semibold uppercase tracking-wider block mb-0.5">Phone</span>
                {effectivePhone.split(',').map((num, i) => (
                  <a key={i} href={`tel:${num.trim()}`} className="text-white hover:text-secondary-light transition-colors">{num.trim()}{i < effectivePhone.split(',').length - 1 ? ', ' : ''}</a>
                ))}
              </p>
              <p className="text-white">
                <span className="text-secondary-light text-xs font-semibold uppercase tracking-wider block mb-0.5">Email</span>
                <a href={`mailto:${effectiveEmail}`} className="text-white hover:text-secondary-light transition-colors">{effectiveEmail}</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-white/5">
        <div className="container py-6">
          <div className="md:hidden mb-4 flex justify-center">
            <img src="/images/cambridge-logo.jpeg" className="h-[4.5rem] w-auto" alt="Cambridge" />
          </div>
          <div className="text-sm text-white/40 text-center">
            {settings.footer_credit || FOOTER_FALLBACK.copyright}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
